import type { ReissueField } from '../components/ReissueDialog';
import type { MedicalReferral, ReissueMedicalReferralRequest } from '../../../services/medicalReferralService';

/** Form mapping for "Corregir referimiento" (void + re-issue, R8) on top of the generic ReissueDialog. */

type Option = { value: string; label: string };
type ReferredDoctor = { id: number; first_name: string; last_name: string };

const PRIORITY_OPTIONS: Option[] = [
  { value: 'routine', label: 'Rutina' },
  { value: 'urgent', label: 'Urgente' },
  { value: 'emergency', label: 'Emergencia' },
];

const doctorOption = (d: ReferredDoctor): Option => ({ value: String(d.id), label: `Dr. ${d.first_name} ${d.last_name}` });

/**
 * `doctors` are the loaded doctors of `selectedSpecialtyId`. The original
 * specialty and doctor stay selectable while their lists load or when the
 * lists lack them, so a controlled select never silently shows another value.
 */
export function referralReissueFields(
  specialties: Array<{ id: number; name: string }>,
  original: MedicalReferral,
  doctors: ReferredDoctor[],
  selectedSpecialtyId: string,
): ReissueField[] {
  const specialtyOptions: Option[] = specialties.map((s) => ({ value: String(s.id), label: s.name }));
  if (!specialties.some((s) => s.id === original.referred_specialty_id)) {
    specialtyOptions.push({ value: String(original.referred_specialty_id), label: original.referred_specialty?.name || 'Especialidad actual' });
  }

  const doctorOptions: Option[] = doctors.map(doctorOption);
  const originalDoctor = original.referred_doctor;
  if (originalDoctor && selectedSpecialtyId === String(original.referred_specialty_id) && !doctors.some((d) => d.id === originalDoctor.id)) {
    doctorOptions.push(doctorOption(originalDoctor));
  }

  return [
    { name: 'referred_specialty_id', label: 'Especialidad', required: true, options: specialtyOptions },
    {
      name: 'referred_doctor_id', label: 'Doctor', dependsOn: 'referred_specialty_id',
      options: [{ value: '', label: 'Cualquier doctor' }, ...doctorOptions],
    },
    { name: 'priority', label: 'Prioridad', options: PRIORITY_OPTIONS },
    { name: 'reason', label: 'Motivo del referimiento', required: true, multiline: true },
    { name: 'clinical_summary', label: 'Resumen clínico', multiline: true },
  ];
}

export function referralReissueInitialValues(original: MedicalReferral): Record<string, string> {
  return {
    referred_specialty_id: String(original.referred_specialty_id),
    referred_doctor_id: original.referred_doctor_id ? String(original.referred_doctor_id) : '',
    reason: original.reason,
    clinical_summary: original.clinical_summary || '',
    priority: original.priority,
  };
}

/** Observations are not editable here and are carried over as-is. */
export function toReferralReissueRequest(
  original: MedicalReferral,
  voidReason: string,
  values: Record<string, string>,
): ReissueMedicalReferralRequest {
  return {
    void_reason: voidReason,
    referred_specialty_id: Number(values.referred_specialty_id),
    referred_doctor_id: values.referred_doctor_id ? Number(values.referred_doctor_id) : undefined,
    reason: values.reason.trim(),
    clinical_summary: values.clinical_summary?.trim() || undefined,
    observations: original.observations || undefined,
    priority: values.priority,
  };
}
